
import { SEO } from "@/components/SEO";
import { Hero } from "@/components/Hero";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ContractorsList } from "@/components/ContractorsList";
import { Contractor } from "@/types/contractor";
import { LoadingState } from "@/components/LoadingState";
import { Card, CardContent } from "@/components/ui/card";
import { QuoteRequestForm } from "@/components/services/QuoteRequestForm";

const BestElectriciansPage = () => {
  const serviceType = "Electricians";
  const pageTitle = `Best ${serviceType} in Oklahoma City, OK`;

  const { data: contractors = [], isLoading } = useQuery({
    queryKey: ['contractors', 'electrical'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contractors')
        .select('*')
        .eq('service_type', 'Electrical')
        .eq('location', 'oklahoma-city')
        .order('featured', { ascending: false })
        .order('rating', { ascending: false });

      if (error) {
        console.error('Error fetching contractors:', error);
        throw error;
      }

      return data as Contractor[];
    },
  });

  const breadcrumbItems = [
    { label: "Services", href: "/services" },
    { label: serviceType, href: "/best-electricians-oklahoma-city" },
  ];

  if (isLoading) {
    return <LoadingState />;
  }

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": pageTitle,
    "description": "Find top-rated, established electricians in Oklahoma City for residential and commercial electrical services. Free quotes, trusted professionals.",
    "url": "https://homeprosokc.com/best-electricians-oklahoma-city",
  };

  return (
    <div className="min-h-screen">
      <SEO
        title={pageTitle}
        description="Find top-rated, established electricians in Oklahoma City for residential and commercial electrical services. Free quotes, trusted professionals."
        canonicalUrl="/best-electricians-oklahoma-city"
        schema={schema}
      />

      <div className="container mx-auto px-4 py-8">
        <Breadcrumbs items={breadcrumbItems} />
        
        <Hero
          title={pageTitle}
          subtitle="Connect with established, reputable electricians for all your residential and commercial electrical needs."
          buttonText="Get a Quote"
          buttonLink="#quote-request"
        />

        <div className="my-12">
          <h2 className="text-2xl font-bold mb-6">Why Choose Our Electricians</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Established Professionals</h3>
                <p>Our electricians are well-established in the Oklahoma City area with proven experience and expertise.</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Emergency Services</h3>
                <p>Many of our professionals offer 24/7 emergency electrical services when you need help right away.</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Free Estimates</h3>
                <p>Get detailed, no-obligation quotes for your electrical projects from our network of trusted professionals.</p>
              </CardContent>
            </Card>
          </div>
        </div>
        
        <div className="my-12">
          <h2 className="text-2xl font-bold mb-6">Our Electrical Services</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Residential Services</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Electrical repairs and troubleshooting</li>
                  <li>Panel upgrades and replacements</li>
                  <li>Lighting installation and repair</li>
                  <li>Outlet and switch installation</li>
                  <li>Ceiling fan installation</li>
                  <li>Home rewiring</li>
                </ul>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Commercial Services</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Commercial electrical installations</li>
                  <li>Office lighting upgrades</li>
                  <li>Electrical maintenance</li>
                  <li>Code compliance updates</li>
                  <li>Emergency power systems</li>
                  <li>Commercial rewiring</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Quote Request Form */}
        <QuoteRequestForm serviceName={pageTitle} />
        
        <div className="mt-12">
          <h2 className="text-2xl font-bold mb-6">Top Electricians in Oklahoma City</h2>
          <ContractorsList
            contractors={contractors}
            isLoading={isLoading}
            viewMode="grid"
            onViewModeChange={() => {}}
            onServiceTypeChange={() => {}}
            onLocationChange={() => {}}
            selectedServiceType=""
          />
        </div>
      </div>
    </div>
  );
};

export default BestElectriciansPage;
